import './EditPromptChain.scss';

import { ChangeEvent, FC, useContext, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import BjxArticleEditor, { ReRenderAction } from 'components/BjxArticleEditor/BjxArticleEditor';
import NavHeading, { NavHeadingProps } from 'components/NavHeading/NavHeading';
import { NavHeadings } from 'constants/Constants';
import { useModifyEntityService } from 'customHooks/useModifyEntityService';
import { useSingleEntity } from 'customHooks/useSingleEntity';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { isEmptyObject } from 'functions/isEmptyObject';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';
import { PromptTemplate } from 'models/Prompt';
import { GptModel, PromptFlow, TargetAction, Visibility } from 'models/PromptFlow';
import { Button, Col, Form, Row, Tab, Tabs } from 'react-bootstrap';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import EditConversationTemplate from '../EditConversationTemplate/EditConversationTemplate';



// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface EditPromptChainProps { }

const EditPromptChain: FC<EditPromptChainProps> = () => {
  const newEntryId = 'new';
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromId = searchParams.get('fromId');
  const promptFlowId: number | undefined = id !== newEntryId ? parseInt(id!, 10) : undefined;
  const copyFromPromptFlowId: number | undefined = fromId ? parseInt(fromId, 10) : undefined;
  const { user } = useAuth0();
  const {
    configRepo,
    setIsProjectSelectable,
    selectedProjectId,
    projects,
    notify,
    sendLog
  } = useContext(AppContext);
  const defaultPromptFlow: PromptFlow = {
    projectId: selectedProjectId!,
    name: '',
    slug: '',
    version: 1,
    metaTitle: '',
    metaKeywords: '',
    metaDescription: '',
    description: '',
    imageUrl: '',
    authorId: user?.sub,
    authorUrl: '',
    visibility: Visibility.Public,
    systemMessage: '',
    examplePrompts: [],
    conversationTemplate: [],
    targetAction: TargetAction.GenerateArticleContent,
    model: GptModel.Gpt35Turbo,
    temperature: 0,
    topP: 0,
    multipleStopSequences: [],
    frequencyPenalty: undefined,
    presencePenalty: undefined,
    logitBias: undefined,
    promptCategoryId: undefined,
  };
  const [cachedPromptFlow, setCachedPromptFlow] = useState<PromptFlow>(defaultPromptFlow);
  const editorRef = useRef<ReRenderAction>(null);
  const promptFlowRef = useRef<PromptFlow>();
  promptFlowRef.current = cachedPromptFlow;
  const onTokenExpired = useTokenExpiredHandler();
  const { createEntity: createPromptFlow, updateEntity: updatePromptFlow } =
    useModifyEntityService<PromptFlow>(
      configRepo!.SCHEDULE_API_BASE_URI,
      Entities.PromptFlows,
      sendLog,
      onTokenExpired,
    );

  useEffect(() => {
    setIsProjectSelectable(false);
  }, []);

  const { entityRequest: promptFlowRequest } = useSingleEntity<PromptFlow>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.PromptFlows,
    promptFlowId,
  );
  const loadedPromptFlow: PromptFlow = promptFlowRequest.entity;
  useEffect(() => {
    const updatedCachedPromptFlow = { ...loadedPromptFlow };
    if (isEmptyObject(updatedCachedPromptFlow)) {
      return;
    }
    setCachedPromptFlow(updatedCachedPromptFlow);
    editorRef.current!.reRender();
  }, [loadedPromptFlow]);

  const { entityRequest: fromPromptFlowRequest } = useSingleEntity<PromptFlow>(
    configRepo!.SCHEDULE_API_BASE_URI,
    Entities.PromptFlows,
    copyFromPromptFlowId
  );
  const copyFromPromptFlow: PromptFlow = fromPromptFlowRequest.entity;
  useEffect(() => {
    const updatedCachedPromptFlow = { ...copyFromPromptFlow };
    if (isEmptyObject(updatedCachedPromptFlow)) {
      return;
    }
    updatedCachedPromptFlow.id = undefined;
    updatedCachedPromptFlow.version = updatedCachedPromptFlow.version ? updatedCachedPromptFlow.version + 1 : 1;
    updatedCachedPromptFlow.conversationTemplate = [...updatedCachedPromptFlow.conversationTemplate];
    updatedCachedPromptFlow.projectId = selectedProjectId!;
    updatedCachedPromptFlow.conversationTemplate.forEach(prompt => {
      // eslint-disable-next-line no-param-reassign
      prompt.id = undefined;
      // eslint-disable-next-line no-param-reassign
      prompt.promptFlowId = undefined;
    });
    setCachedPromptFlow(updatedCachedPromptFlow);
    editorRef.current!.reRender();
  }, [copyFromPromptFlow]);


  const onDocumentFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedCachedPromptFlow = { ...cachedPromptFlow };
    (updatedCachedPromptFlow as any)[event.target.name] = event.target.value;

    setCachedPromptFlow(updatedCachedPromptFlow);
  };

  const onDropdownSelectionChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const updatedCachedPromptFlow = { ...cachedPromptFlow };
    (updatedCachedPromptFlow as any)[event.target.name] = event.target.value;

    setCachedPromptFlow(updatedCachedPromptFlow);
  };


  const onEditorChange = (value: string) => {
    const updatedCachedPromptFlow = { ...promptFlowRef.current! };
    updatedCachedPromptFlow.description = value;
    setCachedPromptFlow(updatedCachedPromptFlow);
  };

  const logAndNotifyPromptFlowSaved = (updatedPromptFlow: PromptFlow) => {
    sendLog(`Prompt Chain has been updated successfully, ${updatedPromptFlow.id}`, LogLevel.info);
    notify('Prompt Chain has been updated successfully.', NotificationType.Success);
  };

  const logAndNotifyPromptFlowSaveFailure = (promptFlowForUpdate: PromptFlow) => {
    notify('Prompt Chain has NOT been updated successfully.', NotificationType.Error);
    sendLog(`Prompt Chain has NOT been updated successfully, ${promptFlowForUpdate.id}`, LogLevel.error, { promptFlowId: promptFlowForUpdate.id });
  };

  const onSave = async (): Promise<{ hasSavedSuccessfully: boolean, promptFlow: PromptFlow }> => {
    let updatedPromptFlow = { ...cachedPromptFlow };
    let hasSavedSuccessfully = false;

    updatedPromptFlow.conversationTemplate = updatedPromptFlow.conversationTemplate.filter(c => c.inputTemplate.trim().length !== 0);

    if (id === newEntryId) {
      const result = await createPromptFlow(updatedPromptFlow);
      if (result) {
        setCachedPromptFlow(result);
        updatedPromptFlow = result; // have to update current context since that's used downstream from here
        navigate(window.location.pathname.replace(newEntryId, result.id!.toString()), {
          replace: true,
        });
        hasSavedSuccessfully = true;
      } else {
        hasSavedSuccessfully = false;
      }
    } else {
      hasSavedSuccessfully = await updatePromptFlow(updatedPromptFlow);
    }

    return { hasSavedSuccessfully, promptFlow: updatedPromptFlow };
  };

  const [previousName, setPreviousName] = useState(cachedPromptFlow.name);
  
  useEffect(() => {
    if (cachedPromptFlow.name !== previousName) {
      const updatedPromptFlow = {...cachedPromptFlow};
      const calculatedSlug = `${cachedPromptFlow.name.replace(/[^\w\s]/gi, '').replaceAll(' ', '-')}-${selectedProjectId}-${cachedPromptFlow.version}`
      updatedPromptFlow.slug = calculatedSlug.replace(/-{2,}/g, '-');
      setPreviousName(cachedPromptFlow.name);
      setCachedPromptFlow(updatedPromptFlow);
    }
  }, [cachedPromptFlow.name, selectedProjectId, cachedPromptFlow.version])

  const GENERAL = 'GENERAL';
  const PUBLIC = 'PUBLIC';
  const PARAMS = 'PARAMS';
  const CHAIN = 'PROMPTCHAIN';
  const [tabKey, setTabKey] = useState(GENERAL);

  const heading: NavHeadingProps = {
    title: id === newEntryId ? 'Create Prompt Chain' : 'Edit Prompt Chain',
    ...NavHeadings.PromptChains,
  };

  const onConversationTemplateChanged = (prompts: PromptTemplate[]) => {
    const updatedPromptFlow = {...cachedPromptFlow};
    updatedPromptFlow.conversationTemplate = prompts;
    setCachedPromptFlow(updatedPromptFlow);
  }

  return (
    <div className="EditPromptFlow">
      <NavHeading {...heading} />
      <Tabs
        activeKey={tabKey}
        onSelect={(k: any) => setTabKey(k)}
        transition={false}
        id="document-editor-tabs"
        className="mb-3 pt-2 doc-tabs"
      >
        <Tab key={1} eventKey={GENERAL} title="General Info">
          <Row>
            <Form.Group className="col-md-6">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={cachedPromptFlow.name}
                onChange={onDocumentFieldChange}
                aria-label="name"
                name="name"
                placeholder="Prompt Chain Name"
                className="mb-2"
                tabIndex={1}
                required
              />
            </Form.Group>
            <Form.Group className="col-md-6">
              <Form.Label>Version</Form.Label>
              <Form.Control
                type="text"
                value={cachedPromptFlow.version}
                aria-label="Version"
                name="version"
                placeholder="Model version"
                className="mb-2"
                tabIndex={2}
                disabled
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group>
              <Form.Label>URL Slug</Form.Label>
              <Form.Control
                type="text"
                value={cachedPromptFlow.slug}
                aria-label="Slug"
                name="slug"
                placeholder="/auto-calculated-url-slug"
                className="mb-2"
                tabIndex={2}
                disabled
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="col-md-6">
              <Form.Label>Model Version</Form.Label>
              <Form.Select
                value={Object.keys(GptModel).find(
                  key => (GptModel as any)[key] === cachedPromptFlow.model
                )}
                onChange={onDropdownSelectionChange}
                aria-label='GPT model to use'
                name="model"
                tabIndex={3}
                required
              >
                {Object.keys(GptModel).map((s) => (
                  <option value={s} key={s}>
                    {(GptModel as any)[s]}
                  </option>
                ))}
              </Form.Select>
              <Form.Text muted>
                For more help on these model types, see {" "}
                <a href="https://platform.openai.com/docs/models/" target="_blank" rel="noreferrer">
                  Gpt Model Reference
                </a>
              </Form.Text>
            </Form.Group>
            <Form.Group className="col-md-6">
              <Form.Label>Visibility</Form.Label>
              <Form.Select
                value={cachedPromptFlow.visibility}
                onChange={onDropdownSelectionChange}
                aria-label='visibility'
                name="visibility"
                tabIndex={4}
                required
              >
                <option value={Visibility.Public}>{Visibility.Public}</option>
                <option value={Visibility.Private}>{Visibility.Private}</option>
              </Form.Select>
              <Form.Text className="text-info">
                {cachedPromptFlow.visibility === Visibility.Public ?
                  (<><strong>Public</strong>: visible across all your projects and publicly.</>) :
                  (<><strong>Private</strong>: only visible to {projects.find(p => p.id === selectedProjectId)?.name}.</>)
                }
              </Form.Text>
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="col-md-6">
              <Form.Label>Target Action</Form.Label>
              <Form.Select
                value={cachedPromptFlow.targetAction}
                onChange={onDropdownSelectionChange}
                aria-label="target action this affects"
                name="targetAction"
                tabIndex={5}
                required
              >
                <option value={TargetAction.GenerateArticleContent}>Generate Article Content</option>
                <option value={TargetAction.GenerateArticleOutline}>Generate Article Outline</option>
                <option value={TargetAction.GenerateArticleTitle}>Generate Article Title</option>
                <option value={TargetAction.GenerateFacebookPost}>Generate Facebook Post</option>
                <option value={TargetAction.GenerateLinkedInPost}>Generate LinkedIn Post</option>
                <option value={TargetAction.GenerateTwitterPost}>Generate Twitter Post</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="col-md-6">
              {/* <Form.Label>Category</Form.Label>
              <Form.Select
                value={cachedPromptFlow.promptCategoryId}
                onChange={onDropdownSelectionChange}
                aria-label='promptCategoryId'
                name="promptCategoryId"
                tabIndex={6}
              >
                <option>Select category</option>
              </Form.Select> */}
            </Form.Group>
          </Row>
        </Tab>

        <Tab key={2} eventKey={PUBLIC} title="Public Model Information">
          <Row>
            <Form.Group className="col-md-12">
              <Form.Label>Meta Title</Form.Label>
              <Form.Control
                type="text"
                value={cachedPromptFlow.metaTitle}
                onChange={onDocumentFieldChange}
                aria-label="meta title"
                name="metaTitle"
                tabIndex={7}
                required
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="col-md-12">
              <Form.Label>Meta Description</Form.Label>
              <Form.Control
                type="text"
                value={cachedPromptFlow.metaDescription}
                onChange={onDocumentFieldChange}
                aria-label="meta description"
                name="metaDescription"
                tabIndex={8}
                required
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="col-md-12">
              <Form.Label>Meta Keywords</Form.Label>
              <Form.Control
                type="text"
                value={cachedPromptFlow.metaKeywords}
                onChange={onDocumentFieldChange}
                aria-label="meta keywords"
                name="metaKeywords"
                tabIndex={9}
                required
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="col-md-12">
              <Form.Label>Author Bio URL</Form.Label>
              <Form.Control
                value={cachedPromptFlow.authorUrl}
                onChange={onDocumentFieldChange}
                aria-label="author biography URL"
                name="authorUrl"
                tabIndex={10}
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="col-md-12">
              <Form.Label className="visually-hidden">Description</Form.Label>
              <div className='rich-editor' >
                <BjxArticleEditor
                  predefinedLinks={[]}
                  placeholderText="Write the long description for your prompt chain here..."
                  content={cachedPromptFlow.description}
                  onEditorChange={onEditorChange}
                  tabIndex={11}
                  ref={editorRef}
                />
              </div>
            </Form.Group>
          </Row>
        </Tab>
        <Tab key={3} eventKey={PARAMS} title="Model Tuning Parameters">
          <Form.Text>
            For help related to these settings, see: <a href="https://platform.openai.com/docs/api-reference/chat/create" target="_blank" rel="noreferrer">Open AI API Docs</a>.
          </Form.Text>
          <Row>
            <Form.Group className="col-md-3">
              <Form.Label>Temperature</Form.Label>
              <Form.Control
                type="number"
                value={cachedPromptFlow.temperature}
                onChange={onDocumentFieldChange}
                aria-label="temperature"
                name="temperature"
                tabIndex={12}
                required
              />
            </Form.Group>
            <Form.Group className="col-md-3">
              <Form.Label>Top P</Form.Label>
              <Form.Control
                type="number"
                value={cachedPromptFlow.topP}
                onChange={onDocumentFieldChange}
                aria-label="Top P"
                name="topP"
                tabIndex={13}
                required
              />
            </Form.Group>
            <Form.Group className="col-md-3">
              <Form.Label>Frequency Penalty</Form.Label>
              <Form.Control
                type="number"
                value={cachedPromptFlow.frequencyPenalty}
                onChange={onDocumentFieldChange}
                aria-label="frequency penalty"
                name="frequencyPenalty"
                tabIndex={14}
                required
              />
            </Form.Group>
            <Form.Group className="col-md-3">
              <Form.Label>Presence Penalty</Form.Label>
              <Form.Control
                type="number"
                value={cachedPromptFlow.presencePenalty}
                onChange={onDocumentFieldChange}
                aria-label="presence penalty"
                name="presencePenalty"
                tabIndex={15}
                required
              />
            </Form.Group>
          </Row>
          <Row>
            <Form.Group className="col-md-12">
              <Form.Label>System Message</Form.Label>
              <Form.Control
                as="textarea"
                value={cachedPromptFlow.systemMessage}
                onChange={onDocumentFieldChange}
                aria-label="system message"
                name="systemMessage"
                tabIndex={16}
                required
              />
            </Form.Group>
          </Row>
        </Tab>
        <Tab key={4} eventKey={CHAIN} title="Conversation Template">
          <Row>
            <EditConversationTemplate promptFlow={cachedPromptFlow} onConversationTemplateChanged={onConversationTemplateChanged} />
          </Row>
        </Tab>
      </Tabs>

      <Row className="fixed-bottom approval-bar">
        <Col md={12} className="btn-sticky-top pull-right">
          <div className="action-buttons">
            <Link
              to="/prompt-chains"
              className="btn btn-light mb-2 mx-2"
              title="Return to prompt chains list"
              reloadDocument
              tabIndex={17}>
              Cancel
            </Link>
            <Button
              type="button"
              onClick={async () => {
                const result = await onSave();
                if (result.hasSavedSuccessfully) {
                  logAndNotifyPromptFlowSaved(result.promptFlow);
                  setCachedPromptFlow(result.promptFlow);
                } else {
                  logAndNotifyPromptFlowSaveFailure(result.promptFlow);
                }
              }}
              className=" btn btn-success mb-2 mx-2"
              tabIndex={18}
              title='Save connection information'
            >
              <i className="bi bi-journal-check px-2 xxs-hide" />
              Save
            </Button>
          </div>
        </Col>
      </Row>

    </div>
  );
}
export default EditPromptChain;
